import type {
  ChatBan,
  ChatDepartment,
  ChatSettings,
  ChatSettingsPreChatFieldsItem,
  ChatShortcut,
  CreateChatBanRequest,
  CreateChatDepartmentRequest,
  CreateChatShortcutRequest,
  MemberChatProfile,
  UpdateChatDepartmentRequest,
  UpdateChatSettingsRequest,
  UpdateChatShortcutRequest,
  UpdateMemberChatProfileRequest,
} from '@nucleus-commerce/api-client/admin';

import type { AppearanceForm } from '../schemas/appearance-schema';
import type { BanDuration, BanForm } from '../schemas/ban-schema';
import type { BehaviourForm } from '../schemas/behaviour-schema';
import type { ChatProfileForm } from '../schemas/chat-profile-schema';
import type { ConsentForm } from '../schemas/consent-schema';
import type { DepartmentForm } from '../schemas/department-schema';
import { emptyToNull, toNumberOrNull } from '../schemas/fields';
import type { FormFieldForm, FormsForm } from '../schemas/forms-schema';
import { WEEK_DAYS, type HoursForm, type WeekDay } from '../schemas/hours-schema';
import type { ShortcutForm, ShortcutScope } from '../schemas/shortcut-schema';

type ChatPatch = UpdateChatSettingsRequest;

/**
 * Each tab owns whole chat settings groups and sends them complete; the chat settings PATCH
 * merges one group deep, so a tab never clears another tab's fields. The one exception is Hours,
 * which also owns `behaviour.autoAssign` and sends it alone (the rest of `behaviour` is
 * Behaviour's). The patch is the body itself, not wrapped in `settings.chat`.
 */

// Appearance ------------------------------------------------------------------------------------

export function fromAppearance(chat: ChatSettings): AppearanceForm {
  const a = chat.appearance;
  return {
    enabled: chat.enabled,
    appearance: {
      shape: a.shape,
      position: a.position,
      mobilePosition: a.mobilePosition,
      primaryColor: a.primaryColor ?? '',
      headerTitle: a.headerTitle ?? '',
      greetingOnline: a.greetingOnline,
      greetingAway: a.greetingAway,
      greetingOffline: a.greetingOffline,
      showAgentAvatars: a.showAgentAvatars,
      logoMediaId: a.logoMediaId,
      attentionGrabberMediaId: a.attentionGrabberMediaId,
    },
  };
}

export function toAppearancePatch(values: AppearanceForm): ChatPatch {
  const a = values.appearance;
  return {
    enabled: values.enabled,
    appearance: {
      shape: a.shape,
      position: a.position,
      mobilePosition: a.mobilePosition,
      primaryColor: emptyToNull(a.primaryColor)?.toLowerCase() ?? null,
      headerTitle: emptyToNull(a.headerTitle),
      greetingOnline: a.greetingOnline.trim(),
      greetingAway: a.greetingAway.trim(),
      greetingOffline: a.greetingOffline.trim(),
      showAgentAvatars: a.showAgentAvatars,
      logoMediaId: a.logoMediaId,
      attentionGrabberMediaId: a.attentionGrabberMediaId,
    },
  };
}

// Hours -----------------------------------------------------------------------------------------

type Week = Record<WeekDay, Array<{ from: string; to: string }>>;

function copyWeek(days: Week): Week {
  const week = {} as Week;
  for (const day of WEEK_DAYS) week[day] = days[day].map(({ from, to }) => ({ from, to }));
  return week;
}

export function fromHours(chat: ChatSettings): HoursForm {
  return {
    hours: {
      enabled: chat.hours.enabled,
      timezone: chat.hours.timezone ?? '',
      days: copyWeek(chat.hours.days),
      offlineWhenNoAgentOnline: chat.hours.offlineWhenNoAgentOnline,
    },
    behaviour: { autoAssign: chat.behaviour.autoAssign },
  };
}

/** The week is replaced whole by the PATCH, so every day goes out, empty ones as `[]`. */
export function toHoursPatch(values: HoursForm): ChatPatch {
  return {
    hours: {
      enabled: values.hours.enabled,
      timezone: emptyToNull(values.hours.timezone),
      days: copyWeek(values.hours.days),
      offlineWhenNoAgentOnline: values.hours.offlineWhenNoAgentOnline,
    },
    behaviour: { autoAssign: values.behaviour.autoAssign },
  };
}

// Forms -----------------------------------------------------------------------------------------

function fromField(field: ChatSettingsPreChatFieldsItem): FormFieldForm {
  return {
    key: field.key,
    kind: field.kind,
    label: field.label,
    required: field.required,
    options: field.options ?? [],
  };
}

/** Options belong to select fields only; other kinds leave the key out. */
export function toField(field: FormFieldForm): ChatSettingsPreChatFieldsItem {
  return {
    key: field.key.trim(),
    kind: field.kind,
    label: field.label.trim(),
    required: field.required,
    ...(field.kind === 'select' ? { options: field.options.map((o) => o.trim()) } : {}),
  };
}

export function fromForms(chat: ChatSettings): FormsForm {
  return {
    preChat: { enabled: chat.preChat.enabled, fields: chat.preChat.fields.map(fromField) },
    offlineForm: {
      enabled: chat.offlineForm.enabled,
      fields: chat.offlineForm.fields.map(fromField),
      confirmationText: chat.offlineForm.confirmationText,
    },
  };
}

export function toFormsPatch(values: FormsForm): ChatPatch {
  return {
    preChat: { enabled: values.preChat.enabled, fields: values.preChat.fields.map(toField) },
    offlineForm: {
      enabled: values.offlineForm.enabled,
      fields: values.offlineForm.fields.map(toField),
      confirmationText: values.offlineForm.confirmationText.trim(),
    },
  };
}

/** A new field's key from its label: "Ring size" → "ring_size". */
export function fieldKeyFromLabel(label: string): string {
  const key = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 40);
  return key;
}

// Consent ---------------------------------------------------------------------------------------

export function fromConsent(chat: ChatSettings): ConsentForm {
  const c = chat.consent;
  return {
    consent: {
      enabled: c.enabled,
      text: c.text ?? '',
      privacyPolicyHandle: c.privacyPolicyHandle ?? '',
      usStateNotice: c.usStateNotice,
      identifyOnLoad: c.identifyOnLoad,
    },
  };
}

export function toConsentPatch(values: ConsentForm): ChatPatch {
  const c = values.consent;
  return {
    consent: {
      enabled: c.enabled,
      text: emptyToNull(c.text),
      privacyPolicyHandle: emptyToNull(c.privacyPolicyHandle),
      usStateNotice: c.usStateNotice,
      identifyOnLoad: c.identifyOnLoad,
    },
  };
}

// Behaviour, emails, retention, SLA, attribution, help centre ------------------------------------------------

const str = (value: number | null) => (value === null ? '' : String(value));

export function fromBehaviour(chat: ChatSettings): BehaviourForm {
  const b = chat.behaviour;
  return {
    behaviour: {
      showTypingToStaff: b.showTypingToStaff,
      soundEnabled: b.soundEnabled,
      allowUploads: b.allowUploads,
      allowRating: b.allowRating,
      allowTranscript: b.allowTranscript,
      suggestedRepliesEnabled: b.suggestedRepliesEnabled,
      idleCloseMinutes: String(b.idleCloseMinutes),
      missedAfterSeconds: String(b.missedAfterSeconds),
      autoCloseResolvedHours: String(b.autoCloseResolvedHours),
      discountCapPerAgentPerDay: String(b.discountCapPerAgentPerDay),
      triggerDiscountCapPerDay: String(b.triggerDiscountCapPerDay),
      freeSizerOffer: b.freeSizerOffer,
    },
    emails: {
      replyWhenAway: chat.emails.replyWhenAway,
      offlineAck: chat.emails.offlineAck,
      autoTranscript: chat.emails.autoTranscriptAfterMinutes !== null,
      // Turning the transcript back on starts from the backend's default delay.
      autoTranscriptAfterMinutes: str(chat.emails.autoTranscriptAfterMinutes) || '60',
      continueUrlTemplate: chat.emails.continueUrlTemplate ?? '',
    },
    retention: {
      purge: chat.retention.purgeClosedAfterMonths !== null,
      purgeClosedAfterMonths: str(chat.retention.purgeClosedAfterMonths) || '24',
    },
    sla: {
      firstResponseSeconds: str(chat.sla.firstResponseSeconds),
      resolutionHours: str(chat.sla.resolutionHours),
    },
    attribution: { windowDays: String(chat.attribution.windowDays) },
    help: { widgetSearch: chat.help.widgetSearch, suggestArticles: chat.help.suggestArticles },
  };
}

/** `behaviour.autoAssign` is left out: the Hours tab owns it. */
export function toBehaviourPatch(values: BehaviourForm): ChatPatch {
  const b = values.behaviour;
  return {
    behaviour: {
      showTypingToStaff: b.showTypingToStaff,
      soundEnabled: b.soundEnabled,
      allowUploads: b.allowUploads,
      allowRating: b.allowRating,
      allowTranscript: b.allowTranscript,
      suggestedRepliesEnabled: b.suggestedRepliesEnabled,
      idleCloseMinutes: Number(b.idleCloseMinutes),
      missedAfterSeconds: Number(b.missedAfterSeconds),
      autoCloseResolvedHours: Number(b.autoCloseResolvedHours),
      discountCapPerAgentPerDay: Number(b.discountCapPerAgentPerDay),
      triggerDiscountCapPerDay: Number(b.triggerDiscountCapPerDay),
      freeSizerOffer: b.freeSizerOffer,
    },
    emails: {
      replyWhenAway: values.emails.replyWhenAway,
      offlineAck: values.emails.offlineAck,
      autoTranscriptAfterMinutes: values.emails.autoTranscript
        ? Number(values.emails.autoTranscriptAfterMinutes)
        : null,
      continueUrlTemplate: emptyToNull(values.emails.continueUrlTemplate),
    },
    retention: {
      purgeClosedAfterMonths: values.retention.purge
        ? Number(values.retention.purgeClosedAfterMonths)
        : null,
    },
    sla: {
      firstResponseSeconds: toNumberOrNull(values.sla.firstResponseSeconds),
      resolutionHours: toNumberOrNull(values.sla.resolutionHours),
    },
    attribution: { windowDays: Number(values.attribution.windowDays) },
    help: { widgetSearch: values.help.widgetSearch, suggestArticles: values.help.suggestArticles },
  };
}

// Shortcuts -------------------------------------------------------------------------------------

export function emptyShortcut(scope: ShortcutScope): ShortcutForm {
  return {
    key: '',
    title: '',
    body: '',
    scope,
    attachments: [],
    productIds: [],
    suggestedReplies: [],
  };
}

export function fromShortcut(shortcut: ChatShortcut): ShortcutForm {
  return {
    key: shortcut.key,
    title: shortcut.title,
    body: shortcut.body,
    scope: shortcut.scope,
    attachments: [...shortcut.attachments],
    productIds: [...shortcut.productIds],
    suggestedReplies: [...shortcut.suggestedReplies],
  };
}

function shortcutFields(values: ShortcutForm) {
  return {
    key: values.key.trim(),
    title: values.title.trim(),
    body: values.body.trim(),
    attachments: values.attachments,
    productIds: values.productIds,
    suggestedReplies: values.suggestedReplies.map((reply) => reply.trim()),
  };
}

export function toCreateShortcut(values: ShortcutForm): CreateChatShortcutRequest {
  return { ...shortcutFields(values), scope: values.scope };
}

/** The scope is fixed once created; the update body has no field for it. */
export function toUpdateShortcut(values: ShortcutForm): UpdateChatShortcutRequest {
  return shortcutFields(values);
}

/** Inserts `{{name}}` at the caret (or replaces the selection) and says where the caret goes. */
export function insertPlaceholder(
  body: string,
  name: string,
  selectionStart: number,
  selectionEnd: number,
): { body: string; caret: number } {
  const token = `{{${name}}}`;
  const start = Math.max(0, Math.min(selectionStart, body.length));
  const end = Math.max(start, Math.min(selectionEnd, body.length));
  return { body: body.slice(0, start) + token + body.slice(end), caret: start + token.length };
}

// Departments -----------------------------------------------------------------------------------

export function emptyDepartment(position: number): DepartmentForm {
  return { name: '', description: '', position: String(position), isDefault: false, memberIds: [] };
}

export function fromDepartment(department: ChatDepartment): DepartmentForm {
  return {
    name: department.name,
    description: department.description ?? '',
    position: String(department.position),
    isDefault: department.isDefault,
    memberIds: [...department.memberIds],
  };
}

export function toCreateDepartment(values: DepartmentForm): CreateChatDepartmentRequest {
  return {
    name: values.name.trim(),
    description: emptyToNull(values.description),
    position: Number(values.position),
    isDefault: values.isDefault,
    memberIds: values.memberIds,
  };
}

/**
 * Members go out only when the editor could show them (the chat agents list loaded); otherwise
 * the list is left alone instead of being replaced by what the form happened to hold.
 */
export function toUpdateDepartment(
  values: DepartmentForm,
  { includeMembers }: { includeMembers: boolean },
): UpdateChatDepartmentRequest {
  return {
    name: values.name.trim(),
    description: emptyToNull(values.description),
    position: Number(values.position),
    isDefault: values.isDefault,
    ...(includeMembers ? { memberIds: values.memberIds } : {}),
  };
}

// Bans ------------------------------------------------------------------------------------------

const DURATION_DAYS: Record<Exclude<BanDuration, 'permanent'>, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export function emptyBan(): BanForm {
  return { target: 'visitor', visitorId: '', ip: '', reason: '', duration: 'permanent' };
}

/** Only the chosen target goes out; a permanent ban has no `expiresAt`. */
export function toCreateBan(values: BanForm, now: Date = new Date()): CreateChatBanRequest {
  const reason = values.reason.trim();
  return {
    ...(values.target === 'visitor'
      ? { visitorId: values.visitorId.trim() }
      : { ip: values.ip.trim() }),
    ...(reason ? { reason } : {}),
    ...(values.duration === 'permanent'
      ? {}
      : {
          expiresAt: new Date(
            now.getTime() + DURATION_DAYS[values.duration] * 86_400_000,
          ).toISOString(),
        }),
  };
}

export function isBanExpired(ban: Pick<ChatBan, 'expiresAt'>, now: Date = new Date()): boolean {
  return ban.expiresAt !== null && new Date(ban.expiresAt).getTime() <= now.getTime();
}

// Member chat profile ---------------------------------------------------------------------------

export function fromChatProfile(profile: MemberChatProfile): ChatProfileForm {
  return {
    displayName: profile.displayName ?? '',
    avatarMediaId: profile.avatarMediaId,
  };
}

export function toChatProfile(values: ChatProfileForm): UpdateMemberChatProfileRequest {
  return {
    displayName: emptyToNull(values.displayName),
    avatarMediaId: values.avatarMediaId,
  };
}
